import { Typography } from "@mui/material";
import { get } from "lodash";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import AppBar from "src/components/AppBar/AppBar";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import GuardedComponent from "src/components/GuardedComponent/GuardedComponent";
import Icon, { IconName } from "src/components/Icon/Icon";
import FilterContainer from "src/components/TableV2/FilterContainer";
import SearchField from "src/components/TableV2/SearchField";
import StatusFilter from "src/components/TableV2/StatusFilter";
import Title from "src/components/TableV2/Title";
import { IUser, UserRole } from "src/store/models/user.model";
import { IRootState } from "src/store/reducers";
import { fetchUsersList, removeUser } from "src/store/reducers/users";
import { openUrl } from "src/util/util.navigation";
import DisTable from "../../components/TableV2/Table";
import * as S from "../../constants/StringConstants";

interface MatchParams {
  userId?: string;
}

interface UsersProps extends RouteComponentProps<MatchParams> {}

const Users: React.FC<UsersProps> = ({ history }) => {
  const [total, setTotal] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deletedId, setDeletedId] = useState<number | null>(null);
  const dispatch = useDispatch();
  const loggedInUserId = useSelector((state: IRootState) => state.auth.user.userID);

  const handleDeleteUserPopup = (userID: number) => {
    setDeletedId(userID);
  };

  const handleDeleteUserConfirm = async () => {
    if (deletedId !== null) {
      await dispatch(removeUser(deletedId));
      setRefreshKey(prev => prev + 1);
      setDeletedId(null);
    }
  };

  const handleDeleteUserCancel = () => {
    setDeletedId(null);
  };

  const columns = [
    {
      key: "name",
      title: S.U_COL_NAME,
      style: { minWidth: "5rem" },
      render: (user: any) => `${user.firstName} ${user.lastName}`,
    },
    {
      key: "email",
      title: S.U_COL_EMAIL,
      style: { minWidth: "5rem" },
    },
    {
      key: "role",
      title: S.U_COL_ROLE,
      style: { minWidth: "5rem" },
    },
    {
      key: "branch",
      title: S.U_COL_BRANCH,
      style: { minWidth: "5rem" },
    },
    {
      key: "rbm",
      title: S.U_COL_RBM,
      style: { minWidth: "5rem" },
    },
    {
      key: "actions",
      title: S.U_COL_DELETE,
      style: { minWidth: "8rem" },
      render: user => {
        if (user.userID === loggedInUserId) {
          return null; // cannot delete yourself
        }

        return user.isDeleted ? (
          <span>{S.U_DELETED}</span>
        ) : (
          <Icon
            name={IconName.DeleteFileBlue}
            size="16px"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleDeleteUserPopup(user.userID);
            }}
          />
        );
      },
    },
  ];

  const userStatusList = [
    { key: "Active", value: 1 },
    { key: "Inactive", value: 0 },
  ];

  const handleUpdate = async ({ page, rows, sort, query }) => {
    try {
      const result: any = await dispatch(fetchUsersList({ page, rows, sort, query }));
      const fetchedUsers = get(result, "value.data.items", []);
      const totalRows = get(result, "value.data.totalRows", 0);

      setTotal(totalRows);

      return {
        total: totalRows,
        data: fetchedUsers,
      };
    } catch (error) {}
  };

  return (
    <div className="table-view-v2__content">
      <div className="table-view__header-buttons-container">
        <AppBar icon={IconName.Users} title={S.U_PAGE_TITLE} />

        <div className="table-view__buttons-container">
          <GuardedComponent authorizedRoles={[UserRole.Admin]}>
            <button className="side-nav__add-button" onClick={() => openUrl("/users/new")}>
              <Icon name={IconName.Plus} />
              {S.NEW_USER}
            </button>
          </GuardedComponent>
        </div>
      </div>

      <DisTable<IUser>
        key={refreshKey}
        columns={columns}
        onRowClick={(user: any) => {
          if (user.id !== loggedInUserId) {
            history.push(`/users/${user.id}`, { user });
          }
        }}
        initialState={{ rows: 25, total }}
        onUpdate={handleUpdate}>
        <FilterContainer
          left={<Title text={S.U_PAGE_TITLE} />}
          right={
            <>
              <SearchField />
              <StatusFilter statusList={userStatusList} useIsDeletedFilter={true} />
            </>
          }></FilterContainer>
      </DisTable>

      {deletedId !== null && (
        <PromptModal
          open
          title={S.U_DEL_MODAL_TITLE}
          confirmText={S.U_CONFIRM_DEL}
          rejectText={S.U_CANCEL_DEL}
          onConfirm={handleDeleteUserConfirm}
          onReject={handleDeleteUserCancel}>
          <Typography variant="h6">{S.U_CONFIRM_DELETE}</Typography>
        </PromptModal>
      )}
    </div>
  );
};

export default Users;
